// src/api/AIAgentApi.js

import axios from 'axios';
import { handleApiError } from './handleApiError'; // Centralized error handler

const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

/**
 * Submits a new agentic job to the backend to be queued for processing.
 * @param {string} token - The user's Firebase auth token.
 * @param {object} payload - The job configuration, including jobName, jobType, influencerIds, and taskParameters.
 * @returns {Promise<{status: boolean, data?: {jobId: string}, message?: string, error?: string}>}
 */
export const startAgentJob = async (token, payload) => {
  const url = `${baseUrl}/agent/api/jobs`;
  console.debug(
    '[AIAgentApi.startAgentJob] Firing request with payload:',
    payload,
  );
  try {
    const response = await axios.post(url, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 202) {
      // 202 Accepted is the correct code for queuing a job
      return { status: true, data: response.data };
    } else {
      return {
        status: false,
        error: response.data?.error || `HTTP_ERROR_${response.status}`,
        message:
          response.data?.message ||
          `Request failed with status ${response.status}.`,
      };
    }
  } catch (error) {
    return handleApiError(error, 'startAgentJob');
  }
};

/**
 * Fetches the historical list of agent jobs for the current user.
 * @param {string} token - The user's Firebase auth token.
 * @returns {Promise<{status: boolean, data?: object[], message?: string, error?: string}>}
 */
export const fetchAgentJobHistory = async (token) => {
  const url = `${baseUrl}/agent/api/jobs`;
  console.debug('[AIAgentApi.fetchAgentJobHistory] Firing request.');
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 200) {
      return { status: true, data: response.data };
    } else {
      return {
        status: false,
        error: response.data?.error || `HTTP_ERROR_${response.status}`,
        message:
          response.data?.message ||
          `Request failed with status ${response.status}.`,
      };
    }
  } catch (error) {
    return handleApiError(error, 'fetchAgentJobHistory');
  }
};

/**
 * Fetches the detailed, influencer-specific results for a single agent job.
 * @param {string} token - The user's Firebase auth token.
 * @param {string} jobId - The ID of the job to fetch details for.
 * @returns {Promise<{status: boolean, data?: object[], message?: string, error?: string}>}
 */
export const fetchAgentJobDetails = async (token, jobId) => {
  const url = `${baseUrl}/agent/api/jobs/${jobId}`;
  console.debug(
    `[AIAgentApi.fetchAgentJobDetails] Firing request for Job ID: ${jobId}`,
  );
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 200) {
      return { status: true, data: response.data };
    } else {
      return {
        status: false,
        error: response.data?.error || `HTTP_ERROR_${response.status}`,
        message:
          response.data?.message ||
          `Request failed with status ${response.status}.`,
      };
    }
  } catch (error) {
    return handleApiError(error, 'fetchAgentJobDetails');
  }
};

export const cancelAgentJob = async (token, jobId) => {
  const url = `${baseUrl}/agent/api/jobs/${jobId}/cancel`;
  console.debug(`[AIAgentApi] Cancelling Job ${jobId}`);
  try {
    const response = await axios.post(
      url,
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
        validateStatus: (status) => status >= 200 && status < 500,
      },
    );
    if (response.status === 200) {
      return { status: true, data: response.data };
    }
    return { status: false, message: response.data.message || 'Failed.' };
  } catch (error) {
    return handleApiError(error, 'cancelAgentJob');
  }
};

export const deleteAgentJob = async (token, jobId) => {
  const url = `${baseUrl}/agent/api/jobs/${jobId}`;
  console.debug(`[AIAgentApi] Deleting Job ${jobId}`);
  try {
    const response = await axios.delete(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });
    if (response.status === 200) {
      return { status: true, data: response.data };
    }
    return { status: false, message: response.data.message || 'Failed.' };
  } catch (error) {
    return handleApiError(error, 'deleteAgentJob');
  }
};

export const sendAgentChatMessage = async (token, payload, signal) => {
  const url = `${baseUrl}/agent/chat-api/chat`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
    signal: signal,
  });

  return response;
};

// --- NEW: Chat Session Management ---

/**
 * Fetches the list of previous Agent Chat Sessions.
 * Used for the "Load previous chat sessions" dropdown.
 */
export const fetchAgentChats = async (token) => {
  const url = `${baseUrl}/agent/chat-api/agent/chats`; // Matches backend route
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.status === 200) {
      return { status: true, data: response.data };
    }
    return { status: false, message: 'Failed to fetch chats.' };
  } catch (error) {
    return handleApiError(error, 'fetchAgentChats');
  }
};

/**
 * Fetches the message history for a specific Agent Chat ID.
 */
export const fetchAgentChatHistory = async (token, chatId) => {
  const url = `${baseUrl}/agent/chat-api/agent/chats/${chatId}/history`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.status === 200) {
      return { status: true, data: response.data };
    }
    return { status: false, message: 'Failed to fetch history.' };
  } catch (error) {
    return handleApiError(error, 'fetchAgentChatHistory');
  }
};
